import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import moment from "moment";
import "./PurchaseSnap.css";

const PurchaseSnapshot = () => {
  const { register, handleSubmit } = useForm();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [skuFilter, setSkuFilter] = useState("");

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");
    setSnapshot(null);
    setSkuFilter("");

    const formattedDate = moment(data.date).format("YYYY-MM-DD");

    try {
      const response = await axios.post(
        INVENTORY_API_BASE_URL + "/searchPurchaseSnapshot",
        {
          date: formattedDate,
        }
      );

      setSnapshot(response.data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch data.");
    } finally {
      setLoading(false);
    }
  };

  const filteredData =
    snapshot?.SnapData.filter((batch) =>
      skuFilter
        ? batch.sku.toLowerCase().includes(skuFilter.toLowerCase())
        : true
    ) || [];

  const downloadCSV = () => {
    if (!snapshot?.SnapData?.length) return;

    const fullData = snapshot.SnapData;

    const summary = [
      ["Overall Quantity", snapshot.OverallQuantity],
      ["Overall Purchase Value (₹)", snapshot.OverallPurchaseValue.toFixed(2)],
      ["Date", moment(snapshot.Dated).format("DD-MM-YYYY")],
      [],
    ];

    const headers = [
      "Batch ID",
      "Purchase Price(₹)",
      "Stock",
      "Value(₹)",
      "SKU",
    ];
    const rows = fullData.map((batch) => [
      batch.batchid,
      batch.purchasePrice.toFixed(2),
      batch.Stock,
      (batch.purchasePrice * batch.Stock).toFixed(2),
      batch.sku,
    ]);

    const csvContent = [...summary, headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      "PurchaseSnapshot_" + moment(snapshot.Dated).format("YYYY-MM-DD") + ".csv"
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <main className="purchase-snapshot-page" aria-busy={loading}>
      <div className="purchase-snapshot__content">
        <header className="purchase-snapshot__header">
          <div>
            <p className="purchase-snapshot__eyebrow">Inventory insights</p>
            <h1>Purchase snapshot</h1>
            <p className="purchase-snapshot__subtitle">
              Review purchase stock and value for a selected date.
            </p>
          </div>
          <span className="purchase-snapshot__header-badge">
            Historical inventory
          </span>
        </header>

        <section
          className="purchase-snapshot__search-card"
          aria-labelledby="purchase-snapshot-search-heading"
        >
          <div className="purchase-snapshot__section-heading">
            <div>
              <h2 id="purchase-snapshot-search-heading">Find a snapshot</h2>
              <p>Choose the date you want to review.</p>
            </div>
            <span className="purchase-snapshot__calendar-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="3.5" y="5.5" width="17" height="15" rx="2.5" />
                <path d="M7.5 3.5v4M16.5 3.5v4M3.5 9.5h17" />
                <path d="M8 13h2m4 0h2M8 16.5h2m4 0h2" />
              </svg>
            </span>
          </div>

          <form
            className="purchase-snapshot__search-form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="purchase-snapshot__field">
              <label htmlFor="purchase-snapshot-date">Snapshot date</label>
              <input
                id="purchase-snapshot-date"
                type="date"
                {...register("date", { required: true })}
              />
            </div>
            <button className="purchase-snapshot__search-button" type="submit">
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="8.8" cy="8.8" r="5.6" />
                <path d="m13 13 4 4" />
              </svg>
              Search snapshot
            </button>
          </form>
        </section>

        {loading && (
          <div
            className="purchase-snapshot__message"
            role="status"
            aria-live="polite"
          >
            <span className="purchase-snapshot__spinner" aria-hidden="true" />
            <strong>Loading snapshot</strong>
            <span>Fetching purchase data for the selected date…</span>
          </div>
        )}

        {error && (
          <div className="purchase-snapshot__error" role="alert">
            <span className="purchase-snapshot__error-mark" aria-hidden="true">
              !
            </span>
            <div>
              <strong>Snapshot could not be loaded</strong>
              <p>{error}</p>
            </div>
          </div>
        )}

        {snapshot && (
          <section className="purchase-snapshot__results" aria-live="polite">
            <div className="purchase-snapshot__summary">
              <article className="purchase-snapshot__summary-card purchase-snapshot__summary-card--quantity">
                <span className="purchase-snapshot__summary-label">
                  Overall quantity
                </span>
                <strong>{snapshot.OverallQuantity}</strong>
                <span className="purchase-snapshot__summary-note">
                  Total units in this snapshot
                </span>
              </article>
              <article className="purchase-snapshot__summary-card purchase-snapshot__summary-card--value">
                <span className="purchase-snapshot__summary-label">
                  Overall purchase value
                </span>
                <strong>
                  ₹{snapshot.OverallPurchaseValue.toFixed(2)}
                </strong>
                <span className="purchase-snapshot__summary-note">
                  Purchase value across all batches
                </span>
              </article>
              <article className="purchase-snapshot__summary-card purchase-snapshot__summary-card--date">
                <span className="purchase-snapshot__summary-label">
                  Snapshot date
                </span>
                <strong className="purchase-snapshot__date-value">
                  {moment(snapshot.Dated).format("DD MMM YYYY")}
                </strong>
                <span className="purchase-snapshot__summary-note">
                  Selected inventory record
                </span>
              </article>
            </div>

            <section
              className="purchase-snapshot__details-panel"
              aria-labelledby="purchase-snapshot-batches-heading"
            >
              <div className="purchase-snapshot__details-heading">
                <div>
                  <h2 id="purchase-snapshot-batches-heading">Batch details</h2>
                  <p>
                    {filteredData.length} of {snapshot.SnapData.length} batches
                  </p>
                </div>
                <div className="purchase-snapshot__tools">
                  <label
                    className="purchase-snapshot__filter"
                    htmlFor="purchase-snapshot-sku"
                  >
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <circle cx="8.8" cy="8.8" r="5.6" />
                      <path d="m13 13 4 4" />
                    </svg>
                    <input
                      id="purchase-snapshot-sku"
                      type="search"
                      placeholder="Filter by SKU"
                      value={skuFilter}
                      onChange={(event) => setSkuFilter(event.target.value)}
                    />
                  </label>
                  <button
                    className="purchase-snapshot__download-button"
                    type="button"
                    onClick={downloadCSV}
                  >
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                      <path d="M10 2.8v9.4m0 0 3.4-3.4M10 12.2 6.6 8.8M3.5 13.7v2.8h13v-2.8" />
                    </svg>
                    Download CSV
                  </button>
                </div>
              </div>

              <div className="purchase-snapshot__table-scroll">
                <table className="purchase-snapshot__table">
                  <caption className="purchase-snapshot__visually-hidden">
                    Purchase snapshot batch details
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Batch ID</th>
                      <th scope="col" className="purchase-snapshot__number-cell">
                        Purchase price
                      </th>
                      <th scope="col" className="purchase-snapshot__number-cell">
                        Stock
                      </th>
                      <th scope="col" className="purchase-snapshot__number-cell">
                        Value
                      </th>
                      <th scope="col">SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length ? (
                      filteredData.map((batch, index) => (
                        <tr key={batch.batchid || index}>
                          <td className="purchase-snapshot__batch-cell">
                            {batch.batchid}
                          </td>
                          <td className="purchase-snapshot__number-cell">
                            ₹{batch.purchasePrice.toFixed(2)}
                          </td>
                          <td className="purchase-snapshot__number-cell">
                            {batch.Stock}
                          </td>
                          <td className="purchase-snapshot__value-cell">
                            ₹{(batch.purchasePrice * batch.Stock).toFixed(2)}
                          </td>
                          <td>
                            <span className="purchase-snapshot__sku">
                              {batch.sku}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          className="purchase-snapshot__empty"
                          colSpan="5"
                        >
                          <strong>No matching batches</strong>
                          <span>
                            Try another SKU or clear the search to see all
                            batches.
                          </span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <footer className="purchase-snapshot__table-footer">
                <span>
                  Showing {filteredData.length}{" "}
                  {filteredData.length === 1 ? "batch" : "batches"}
                </span>
                <span>Purchase snapshot · {moment(snapshot.Dated).format("DD-MM-YYYY")}</span>
              </footer>
            </section>
          </section>
        )}

        {!snapshot && !loading && !error && (
          <div className="purchase-snapshot__message purchase-snapshot__message--empty">
            <span className="purchase-snapshot__empty-mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none">
                <rect x="5" y="6" width="22" height="21" rx="3" />
                <path d="M10 3.5v5M22 3.5v5M5 12h22M10 17h4m4 0h4M10 21h4" />
              </svg>
            </span>
            <strong>Select a date to get started</strong>
            <span>The purchase snapshot and batch details will appear here.</span>
          </div>
        )}
      </div>
    </main>
  );
};

export default PurchaseSnapshot;
